import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useHrAccess } from "../api/hrApi";
import type { HrMeResponse } from "../types/hr.contract";
import {
  HR_ATTENDANCE_TABS,
  HR_BASE_PATH,
  HR_HOME_PATH,
  HR_NAV_ITEMS,
  HR_NAV_SECTIONS,
  isHrPath,
  type HrNavItem,
  type HrRequirement,
} from "../config/hr.nav";

const ATTENDANCE_PATH = `${HR_BASE_PATH}/attendance`;

/**
 * The capabilities that open an entry — holding ANY one of them is enough.
 *
 * One entry, one requirement, with one exception: the Attendance screen hosts
 * four sub-tabs that gate separately (register + work hours on attendance, WFH
 * and Leave on their own modules). It opens for whoever holds ANY of them;
 * otherwise a role granted leave but not the register would hold a capability
 * with no screen to use it on. The page itself shows only the tabs held.
 *
 * An `anyHrAccess` entry (Approvals) opens for whoever can open ANY HR screen:
 * every entry's requirement plus every Attendance sub-tab's.
 *
 * The sidebar AND the route guard both read this, so a visible entry can never
 * lead to a "no access" page and the guard can never bounce a user off a page
 * the sidebar offered them.
 */
export function hrEntryRequirements(item: HrNavItem): HrRequirement[] {
  const all = item.anyHrAccess
    ? [
        ...HR_NAV_ITEMS.map((i) => i.requires),
        ...HR_ATTENDANCE_TABS.map((t) => t.requires),
      ]
    : item.path === ATTENDANCE_PATH
      ? [item.requires, ...HR_ATTENDANCE_TABS.map((t) => t.requires)]
      : [item.requires];
  const seen = new Set<string>();
  return all.filter((r) => {
    const key = `${r.module}.${r.action}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * True when the caller holds the capability at any level ("own" counts —
 * self-service screens show the caller's own rows). Admins bypass, exactly as
 * they do on the server (CLAUDE.md rule 10: never double-gate them).
 */
export function hrHolds(
  me: HrMeResponse | null | undefined,
  req: HrRequirement,
): boolean {
  if (!me) return false;
  if (me.isAdmin) return true;
  const level = me.access?.[req.module]?.[req.action];
  return !!level && level !== "none";
}

export function hrHoldsAny(
  me: HrMeResponse | null | undefined,
  reqs: readonly HrRequirement[],
): boolean {
  return reqs.some((r) => hrHolds(me, r));
}

/**
 * A listed sidebar entry with its live count resolved.
 *
 * The count rides INSIDE `label` ("Approvals (3)"), not as a separate pill:
 * the sidebar that renders these entries is the CRM's shared one
 * (`components/layout/sidebar`), which reads `label` and `icon` only. `badge`
 * is carried alongside (null = none) so that renderer can draw a real pill
 * later without another change here.
 */
export type HrNavEntry = Omit<HrNavItem, "badge"> & { badge: number | null };

function toNavEntry(item: HrNavItem, me: HrMeResponse): HrNavEntry {
  const count = item.badge ? item.badge(me) : 0;
  const badge = count > 0 ? count : null;
  return {
    ...item,
    label: badge === null ? item.label : `${item.label} (${badge.toLocaleString("en-IN")})`,
    badge,
  };
}

/**
 * Capability-filtered HR navigation, plus whether the HR Suite is active.
 *
 * Gating is on the SERVER-RESOLVED access map from `GET /suites/hr/me`
 * (`access_config.global.hr_*` → all | own | none), never on route keys — one
 * source of truth, and the server enforces the same map on every
 * `/suites/hr/*` endpoint. This is UI hygiene so people are not shown doors
 * that 403; the server is the boundary.
 *
 * For a tenant without HR only `GET /suites` runs (`/me` stays disabled), and
 * everything here comes back empty/false — the CRM shell then renders exactly
 * as it does without the suite.
 */
export function useHrNav() {
  const { pathname } = useLocation();
  const { enabled, isLoading, me } = useHrAccess();

  const allowed = useMemo<HrNavItem[]>(
    () =>
      enabled && me
        ? HR_NAV_ITEMS.filter((item) => hrHoldsAny(me, hrEntryRequirements(item)))
        : [],
    [enabled, me],
  );

  // Listed entries: permitted, marked for the nav, and — where an entry has
  // its own condition (Approvals: only for people who decide requests) —
  // meeting it. Each carries its live count.
  const navItems = useMemo<HrNavEntry[]>(
    () =>
      me
        ? allowed
            .filter((item) => item.showInNav && (!item.navWhen || item.navWhen(me)))
            .map((item) => toNavEntry(item, me))
        : [],
    [allowed, me],
  );

  /** Accordion sections in display order, empty ones dropped. */
  const sections = useMemo(
    () =>
      HR_NAV_SECTIONS.map((section) => ({
        ...section,
        items: navItems.filter((item) => item.group === section.group),
      })).filter((section) => section.items.length > 0),
    [navItems],
  );

  /** The footer entry (HR's own Settings), or null if this user lacks it. */
  const pinnedItem = useMemo(
    () => navItems.find((item) => item.group === "pinned") ?? null,
    [navItems],
  );

  /** HR is on for the tenant AND this user can open at least one HR screen. */
  const canAccessHr = enabled && allowed.length > 0;

  /**
   * First screen the user may actually land on, in sidebar order; a
   * drill-down-only screen (Employees) when that is all they hold.
   */
  const homePath =
    sections[0]?.items[0]?.path ??
    pinnedItem?.path ??
    allowed[0]?.path ??
    HR_HOME_PATH;

  return {
    /**
     * True when the current URL is inside the HR Suite and the user may be
     * there (or it is not known yet). A user who cannot use HR keeps the CRM
     * sidebar while the route guard sends them on their way.
     */
    isHrActive: isHrPath(pathname) && (isLoading || canAccessHr),
    /** Visible, permitted sidebar entries in display order. */
    navItems,
    /** Those entries grouped into the sidebar's accordion sections. */
    sections,
    /** Pinned footer entry, or null. */
    pinnedItem,
    /** All permitted HR entries, including hidden drill-down routes. */
    allowedItems: allowed,
    /**
     * True when the user may enter HR at all. Drives whether the CRM shell
     * offers a way in (the suite switcher) — no capability, no entry point.
     */
    canAccessHr,
    /** Landing route when entering the suite without a specific target. */
    homePath,
    /** Suites / `/me` still loading — nothing above is final yet. */
    isLoading,
    /** HR is switched on for the tenant (says nothing about this user). */
    isHrEnabled: enabled,
  };
}
